import type { Router } from 'vue-router'

export interface ChunkReloadOptions {
  /** Sự cố im lặng quá lâu này thì lần lỗi sau được coi là sự cố MỚI, đếm lại từ đầu (mặc định 5 phút). */
  resetAfterMs?: number
  /** Số lần reload tối đa trong MỘT sự cố (mặc định 1). */
  maxAttempts?: number
  storageKey?: string
  onReloadBlocked?: (err: unknown) => void
  reload?: () => void
  now?: () => number
  storage?: Storage
}

const DEFAULT_RESET_AFTER_MS = 300_000
const DEFAULT_MAX_ATTEMPTS = 1
const DEFAULT_STORAGE_KEY = 'kyn:chunk-reload'

// Đã bắn reload thì trang sắp rời đi — mọi lỗi sau đó là cùng một sự cố. Cờ này chết theo trang nên
// KHÔNG phải cờ sticky: tải lại là nó về false.
let reloadIssued = false

function readMessage(err: unknown): string {
  if (typeof err === 'string') return err.toLowerCase()
  if (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message.toLowerCase()
  }
  return ''
}

function readName(err: unknown): string {
  if (err && typeof err === 'object' && typeof (err as { name?: unknown }).name === 'string') {
    return (err as { name: string }).name
  }
  return ''
}

// Neo theo hình dạng THẬT của webpack ("Loading chunk 3 failed."). Khớp lỏng bằng includes('loading
// chunk') sẽ nuốt lỗi nghiệp vụ có chữ chunk (upload theo khối) rồi reload thay vì báo lỗi.
const WEBPACK_CHUNK_RE = /loading (css )?chunk \S+ failed/

// 5 mẫu bắt buộc — bỏ nhóm nào là bundler/trình duyệt đó trắng màn không báo (Chrome/Firefox, Safari,
// Vite CSS chunk, webpack ChunkLoadError, webpack biến thể theo message).
export function isChunkLoadError(err: unknown): boolean {
  if (readName(err) === 'ChunkLoadError') return true
  const message = readMessage(err)
  if (!message) return false
  if (message.includes('dynamically imported module')) return true
  if (message.includes('importing a module script failed')) return true
  if (message.includes('unable to preload css')) return true
  return WEBPACK_CHUNK_RE.test(message)
}

interface Attempt {
  ts: number
  count: number
}

function readAttempt(storage: Storage | undefined, key: string): Attempt | null {
  try {
    const raw = storage?.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const { ts, count } = parsed as Partial<Attempt>
    if (typeof ts !== 'number' || typeof count !== 'number') return null
    if (Number.isNaN(ts) || Number.isNaN(count)) return null
    return { ts, count }
  } catch {
    return null
  }
}

// Đếm số lần + mốc hết hạn sự cố, KHÔNG phải cờ boolean và cũng KHÔNG phải trần cả đời tab: cả hai
// cách đó đều chặn luôn reload ở lần deploy SAU trong cùng phiên tab.
// CHỈ reload khi ĐỌC LẠI được mốc vừa ghi. Ghi hỏng (Safari private mode, iframe sandbox thiếu
// allow-same-origin) mà vẫn reload thì mỗi vòng lại đọc ra "chưa thử lần nào" ⇒ tab quay vô hạn.
function reloadWithinWindow(err: unknown, options: ChunkReloadOptions): void {
  const {
    resetAfterMs = DEFAULT_RESET_AFTER_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    storageKey = DEFAULT_STORAGE_KEY,
    onReloadBlocked,
    reload = () => window.location.reload(),
    now = Date.now,
  } = options

  if (reloadIssued) return

  let storage: Storage | undefined
  try {
    storage = options.storage ?? window.sessionStorage
  } catch {
    storage = undefined
  }

  const currentTime = now()
  const prev = readAttempt(storage, storageKey)
  const count = prev && currentTime - prev.ts <= resetAfterMs ? prev.count : 0

  if (count >= maxAttempts) {
    onReloadBlocked?.(err)
    return
  }

  const next: Attempt = { ts: currentTime, count: count + 1 }
  let marked = false
  try {
    storage?.setItem(storageKey, JSON.stringify(next))
    const back = readAttempt(storage, storageKey)
    marked = back?.ts === next.ts && back?.count === next.count
  } catch {
    marked = false
  }

  if (!marked) {
    onReloadBlocked?.(err)
    return
  }

  reloadIssued = true
  reload()
}

export function handleChunkLoadError(err: unknown, options: ChunkReloadOptions = {}): boolean {
  if (!isChunkLoadError(err)) return false
  reloadWithinWindow(err, options)
  return true
}

// router.onError bắt MỌI lỗi điều hướng (guard throw, lỗi setup component), không riêng chunk-error
// — phải đi qua isChunkLoadError bên trong handleChunkLoadError, bỏ điều kiện đó là nuốt lỗi thật.
// 'vite:preloadError' chỉ Vite phát khi preload dynamic import fail nên khỏi cần khớp message; sự
// kiện này không tồn tại ở webpack — ở đó chỉ nhánh router.onError chạy.
export function installChunkReloadGuard(options: ChunkReloadOptions & { router?: Router }): () => void {
  const { router, ...chunkOptions } = options

  const unregisterRouter = router?.onError((err) => {
    handleChunkLoadError(err, chunkOptions)
  })

  const onPreloadError = (event: Event) => {
    event.preventDefault()
    reloadWithinWindow((event as { payload?: unknown }).payload ?? event, chunkOptions)
  }
  window.addEventListener('vite:preloadError', onPreloadError)

  return () => {
    unregisterRouter?.()
    window.removeEventListener('vite:preloadError', onPreloadError)
  }
}

/** Chỉ dùng trong test — cờ `reloadIssued` sống theo vòng đời trang, test phải tự dọn giữa các ca. */
export function __resetChunkReloadStateForTests(): void {
  reloadIssued = false
}
